import { useCallback, useRef } from 'react';
import { useToast } from '../components/contexts/ToastContext';
import { downloadJSONAsXLSX } from '../utils/downloadHelpers';
import { getDateKey } from '../utils/dateHelpers';

/**
 * Hook para exportar datos a archivo Excel
 *
 * @param {Object} params - Parámetros de configuración
 * @param {Array<Object>} params.data - Datos a exportar (rawData de usePaginatedEntity)
 * @param {Function} params.mapFn - Transforma cada fila a objeto con headers como keys
 * @param {string} params.sheetName - Nombre de la hoja Excel
 * @param {string} [params.filePrefix] - Prefijo del archivo (default: sheetName en snake_case)
 * @param {Array<{wch: number}>} params.columnWidths - Anchos de columnas
 * @param {string} params.entityLabel - Etiqueta para toasts (ej: 'equipos')
 * @returns {Function} Función exportToExcel memoizada
 */
export function useExportExcel({ data, mapFn, sheetName, filePrefix, columnWidths, entityLabel }) {
  const { showToast } = useToast();
  const mapFnRef = useRef(mapFn);
  mapFnRef.current = mapFn;
  const columnWidthsRef = useRef(columnWidths);
  columnWidthsRef.current = columnWidths;

  const exportToExcel = useCallback(() => {
    if (data.length === 0) {
      showToast(`No hay ${entityLabel} para exportar`, 'warning');
      return;
    }
    try {
      const rows = data.map(mapFnRef.current);
      const timestamp = getDateKey(new Date());
      const prefix = filePrefix || `${sheetName.toLowerCase().replace(/\s+/g, '_')}_`;
      downloadJSONAsXLSX(rows, sheetName, `${prefix}${timestamp}`, columnWidthsRef.current);
      showToast(`Exportados ${rows.length} ${entityLabel}`, 'success');
    } catch (err) {
      showToast('Error al generar el archivo Excel', 'error');
    }
  }, [data, entityLabel, filePrefix, sheetName, showToast]);

  return exportToExcel;
}
